import * as THREE from "three";
import { CSS2DObject } from "three/addons/renderers/CSS2DRenderer.js";
import { toRaw } from "vue";
import API from "@/api/API";
import { viewerColors } from "@/utils/viewer-colors";
import { hasPermission, Permission } from "@/constants/permissions";
import {
  COORDINTATE_LINE_COLOR_X,
  COORDINTATE_LINE_COLOR_Y,
  OCCLUSION_HIDDEN_LAYER,
  OCCLUSION_RAY_TOLERANCE_M,
  ANNOTATION_DEPTH_TOLERANCE_M,
  FAINT_LABEL_OPACITY,
  SEGMENT_LABEL_FAINT_FRACTION,
  RENDERING_ORDER,
  SELECTED_LABEL_BOOST,
} from "@/constants";
import {
  dropHitsOnHiddenAnnotations,
  isAnnotationSelected,
  markAsAnnotation,
  setAnnotationSelected,
  setOccludedEmphasis,
  setOccludedGhostVisible,
} from "@/utils/annotation-depth";
import { ElMessage } from "element-plus";

export const createMeasurementMarker = function (referencePoint) {
  const wrapper = this.createReactivePoint(referencePoint, viewerColors.POINT_COLOR);

  return wrapper;
};

export const createCoordinateMeasurementLine = function (
  firstPoint,
  secondPoint,
  color,
  vertical
) {
  const lineGeometry = new THREE.BufferGeometry().setFromPoints([
    firstPoint,
    secondPoint,
  ]);

  const lineMaterial = new THREE.LineBasicMaterial({
    color: color,
    transparent: true,
  });

  const line = new THREE.Line(lineGeometry, lineMaterial);

  // The x/y projection lines are a readout of the measurement, not something
  // standing on the roof, so they stay legible through whatever is in front.
  markAsAnnotation(line, {
    renderOrder: RENDERING_ORDER.AUXILIARY_LINE,
    occludes: false,
  });

  const distance = secondPoint.distanceTo(firstPoint);
  var div = document.createElement("div");
  div.className = "measurementLabelDetail";

  var span = document.createElement("span");
  span.textContent = `${this.$n(distance, "decimal")}`;

  var image = document.createElement("img");
  image.src = `/assets/model/${
    vertical ? "vertical_axis" : "horizontal_axis"
  }.svg`;
  image.className = "q-mr-xs";

  div.appendChild(image);
  div.appendChild(span);

  let label = new CSS2DObject(div);
  label.position.lerpVectors(secondPoint, firstPoint, 0.5);
  label.layers.set(0);
  label.renderOrder = RENDERING_ORDER.AUXILIARY_LABEL;

  return {
    line,
    label,
    distance,
  };
};

export const updateCoordinateMeasurementLine = function (
  line,
  label,
  firstPoint,
  secondPoint
) {
  line.geometry.setFromPoints([firstPoint, secondPoint]);
  line.computeLineDistances();
  const distance = secondPoint.distanceTo(firstPoint);
  let div = label.element;
  let span = document.createElement("span");
  span.textContent = `${this.$n(distance, "decimal")}`;
  div.replaceChild(span, div.lastChild);
  label.position.lerpVectors(secondPoint, firstPoint, 0.5);
};

export const addMarker = async function (event) {
  this.suppressClickDefault(event);

  if (this.disableClick(event)) return;

  this.setMousePosition(event);

  if (this.isMeasurementSelected) {
    this.hideMeasurementDetails();
    this.deselectSidebarDistances();
  }

  let intersects = this.raycaster.intersectObject(this.modelObject.children[0]);
  if (intersects.length < 1) return;
  this.disableDragControls();

  let o = intersects[0];

  let pIntersect = o.point.clone();
  this.scene.worldToLocal(pIntersect);

  if (this.measurements.length) {
    let pointIntersects = this.raycaster.intersectObject(
      this.measurements[this.measurements.length - 1].firstPoint
    );

    if (pointIntersects.length > 0) {
      return;
    }
  }

  const marker = this.createMeasurementMarker(pIntersect);

  if (this.dragOn) return;

  // first point placement
  if (
    this.measurements.length === 0 ||
    this.measurements[this.measurements.length - 1].secondPoint
  ) {
    this.measurements.push({ firstPoint: marker, show: true });

    let currentMeasurement = this.measurements[this.measurements.length - 1];

    let tempMarker = this.createMeasurementMarker(pIntersect);
    currentMeasurement.tempPoint = tempMarker;

    this.camera.lookAt(currentMeasurement.firstPoint.position);
    const firstPoint = new THREE.Vector3();
    const secondPoint = new THREE.Vector3();

    currentMeasurement.firstPoint.getWorldPosition(firstPoint);
    currentMeasurement.tempPoint.getWorldPosition(secondPoint);

    let points = [firstPoint, secondPoint];

    this.tempLine = this.createReactiveThickLine(points, 4.0, true);
    const line = this.tempLine;
    // add label on line
    let label = this.createLabelBetweenTwoPoints(
      firstPoint,
      secondPoint,
      0.5,
      true,
      line
    );

    const {
      line: xAxisLine,
      label: xAxisLabel,
      distance: verticalComponent,
    } = this.createCoordinateMeasurementLine(
      firstPoint,
      new THREE.Vector3(firstPoint.x, secondPoint.y, firstPoint.z),
      COORDINTATE_LINE_COLOR_X,
      true
    );
    currentMeasurement.verticalComponent = verticalComponent.toFixed(2);

    const {
      line: yAxisLine,
      label: yAxisLabel,
      distance: horizontalComponent,
    } = this.createCoordinateMeasurementLine(
      new THREE.Vector3(firstPoint.x, secondPoint.y, firstPoint.z),
      new THREE.Vector3(secondPoint.x, secondPoint.y, secondPoint.z),
      COORDINTATE_LINE_COLOR_Y,
      false
    );
    currentMeasurement.horizontalComponent = horizontalComponent.toFixed(2);

    this.measurements[this.measurements.length - 1].tempLine = line;
    this.measurements[this.measurements.length - 1].tempLabel = label;
    this.measurements[this.measurements.length - 1].xAxisLine = xAxisLine;
    this.measurements[this.measurements.length - 1].yAxisLine = yAxisLine;
    this.measurements[this.measurements.length - 1].xAxisLabel = xAxisLabel;
    this.measurements[this.measurements.length - 1].yAxisLabel = yAxisLabel;

    // add point undo callback function
    this.undoStack.push({
      action: "ADD_MEASUREMENT_POINT",
      measurement: currentMeasurement,
    });
    this.resetRedoStack();

    // second point placement
  } else {
    let currentMeasurement = this.measurements[this.measurements.length - 1];

    currentMeasurement.secondPoint = marker;

    const firstPoint = new THREE.Vector3();
    const secondPoint = new THREE.Vector3();

    currentMeasurement.firstPoint.getWorldPosition(firstPoint);
    currentMeasurement.secondPoint.getWorldPosition(secondPoint);

    let points = [firstPoint, secondPoint];

    this.removeObjectFromScene(this.tempLine);

    let line = this.createReactiveThickLine(points, 4.0);
    this.measurementLine = line;

    this.camera.lookAt(currentMeasurement.firstPoint.position);

    let label = this.createLabelBetweenTwoPoints(
      firstPoint,
      secondPoint,
      0.5,
      true,
      line
    );

    this.removeObjectFromScene(currentMeasurement.tempLine);
    this.removeObjectFromScene(currentMeasurement.tempLabel);

    this.scene.add(line);
    this.scene.add(label);

    currentMeasurement.line = line;
    currentMeasurement.label = label;
    currentMeasurement.distance = label.element.distance;

    if (hasPermission(Permission.Create_Objects)) {
      try {
        const { data } = await this.createMeasurementObject([
          firstPoint,
          secondPoint,
        ]);
        currentMeasurement.id = data;
      } catch (e) {}
    }
    // removed due to mk3 split release
    this.distanceMeasurementActivate();
    this.showMeasurementDetails(currentMeasurement, true, false, false);
    // this.deselectSidebarDistances();
    this.resetUndoStack();
    this.resetRedoStack();
  }

  this.markers.push(marker);
  this.additionalMarkers.push(marker);

  if (this.isMeasurementSelected) {
    this.disableDragControls();
    this.enableDragControls();
  }
  this.scene.add(marker);
};

export const detectmeasurementIntersectionForCursorChange = function (
  event,
  persist = false
) {
  this.setMousePosition(event);
  const measurementObjects = [];

  this.measurements.forEach((measurement) => {
    measurementObjects.push(measurement.line);
  });

  let intersects = dropHitsOnHiddenAnnotations(
    this.raycaster.intersectObjects(measurementObjects)
  );

  if (intersects.length > 0) {
    setTimeout(() => {
      this.changeCursorToPointer();
    }, 0);
  }
};

export const detectMeasurementIntersection = function (event, persist = false) {
  if (this.dragOn) return;

  // Check if all measurements are  hidden
  const measurementsVisible =
    this.$store?.state?.section?.config?.distances_visible;
  if (!measurementsVisible && hasPermission(Permission.View_PersonalSettings)) {
    // If all measurements are  hidden, don't detect intersections
    return [];
  }

  this.setMousePosition(event);
  const measurementObjects = [];
  this.measurements
    .filter((measurement) => measurement.line && measurement.show !== false)
    .forEach((measurement) => {
      measurementObjects.push(measurement.line);
    });

  let intersects = dropHitsOnHiddenAnnotations(
    this.raycaster.intersectObjects(measurementObjects)
  );

  if (intersects.length > 0) {
    const line = intersects[0].object;
    const measurement = this.measurements.find(
      (item) => item.line.id === line.id
    );

    this.showMeasurementDetails(measurement, persist);
    this.temporaryMeasurement = measurement;

    setTimeout(() => {
      this.changeCursorToPointer();
    }, 0);
    if (persist) this.measurementDetailsPersistent = true;
  } else {
    if (!this.measurementDetailsPersistent) {
      this.hideMeasurementDetails();
      this.restoreDefaultCursor();
    } else if (persist) {
      if (this.measurementLabelClicked) {
        this.measurementLabelClicked = false;
        return;
      }
      this.deselectSidebarDistances();
      this.hideMeasurementDetails();
      this.measurementDetailsPersistent = false;
    } else if (
      this.selectedMeasurement &&
      this.temporaryMeasurement &&
      this.selectedMeasurement.id !== this.temporaryMeasurement.id
    ) {
      let mousemove = false;
      if (event.movementX !== 0 || event.movementY !== 0) {
        mousemove = true;
      }
      this.hideMeasurementDetails([this.temporaryMeasurement], mousemove);
      this.restoreDefaultCursor();
    }
  }
  return intersects;
};

export const holdDistanceMeasurement = function () {
  this.cleanNavigationSetup();
  this.disableDefaultNavigation();

  document.removeEventListener(
    "click",
    this.detectPersistentMeasurementIntersection,
    false
  );

  if (this.active === 1)
    document.removeEventListener("click", this.addMarker, false);
};

export const unholdDistanceMeasurement = function () {
  this.restoreDefaultNavigation();

  setTimeout(() => {
    document.addEventListener(
      "click",
      this.detectPersistentMeasurementIntersection,
      false
    );
    if (this.active === 1)
      document.addEventListener("click", this.addMarker, false);

    // this.showMeasurement(measurement.line.id, false);
    // measurement is not defined here not sure why this was added
  }, 0);
};

export const detectPersistentMeasurementIntersection = function (event) {
  this.detectMeasurementIntersection(event, true);
};

export const inDistanceCreationMode = function () {
  if (this.measurements.length === 0) return false;

  const lastMeasurement = this.measurements[this.measurements.length - 1];

  if (lastMeasurement.secondPoint) return false;

  return true;
};

export const detectMeasurementIntesectionForCursorChange = function (event) {
  if (this.inDistanceCreationMode()) return this.changeCursorToCrosshair();

  this.setMousePosition(event);
  const measurementObjects = [];
  this.measurements
    .filter(
      (measurement) =>
        measurement.line && measurement.line.visible && measurement.secondPoint
    )
    .forEach((measurement) => {
      measurementObjects.push(measurement.line);
      measurementObjects.push(measurement.firstPoint);
      measurementObjects.push(measurement.secondPoint);
    });
  if (measurementObjects.length === 0) return false;

  let intersects = dropHitsOnHiddenAnnotations(
    this.raycaster.intersectObjects(measurementObjects)
  );
  if (intersects.length === 0) {
    return this.changeCursorToCrosshair();
  }

  this.changeCursorToPointer();
};

export const updatePreliminaryPointPosition = function (event) {
  if (this.dragOn) return;

  if (this.shouldThrottle()) return;

  if (
    this.measurements.length > 0 &&
    !this.measurements[this.measurements.length - 1].secondPoint
  ) {
    this.setMousePosition(event);

    this.handleDistanceMeasurementRaycastResult(this.raycastModelPoint());
  }
};

export const handleDistanceMeasurementRaycastResult = function (
  intersectionPoint
) {
  if (!intersectionPoint) return;

  let pIntersect = new THREE.Vector3(
    intersectionPoint.x,
    intersectionPoint.y,
    intersectionPoint.z
  );

  this.scene.worldToLocal(pIntersect);

  const measurement = this.measurements[this.measurements.length - 1];

  let marker = measurement.tempPoint;
  marker.position.x = pIntersect.x;
  marker.position.y = pIntersect.y;
  marker.position.z = pIntersect.z;

  const firstPoint = new THREE.Vector3();
  const secondPoint = new THREE.Vector3();

  measurement.firstPoint.getWorldPosition(firstPoint);
  measurement.tempPoint.getWorldPosition(secondPoint);

  let points = [firstPoint, secondPoint];

  if (this.tempLine) {
    this.updateLinePosition(this.tempLine, points);
  }

  if (measurement.secondPoint) {
    return this.removeObjectFromScene(this.tempLine);
  }

  const line = this.tempLine;
  const label = measurement.tempLabel;

  const distance = this.updateLabelBetweenTwoPoints(
    label,
    firstPoint,
    secondPoint,
    0.5,
    false
  );

  const xAxisLine = measurement.xAxisLine;
  const xAxisLabel = measurement.xAxisLabel;

  const yAxisLine = measurement.yAxisLine;
  const yAxisLabel = measurement.yAxisLabel;

  this.updateCoordinateMeasurementLine(
    xAxisLine,
    xAxisLabel,
    firstPoint,
    new THREE.Vector3(firstPoint.x, secondPoint.y, firstPoint.z)
  );

  this.updateCoordinateMeasurementLine(
    yAxisLine,
    yAxisLabel,
    new THREE.Vector3(firstPoint.x, secondPoint.y, firstPoint.z),
    new THREE.Vector3(secondPoint.x, secondPoint.y, secondPoint.z)
  );

  const object = this.scene.getObjectById(line.id);

  // only show measurement details when the second point is at a certain distance from the first point
  if (!object && distance > 0.5) {
    this.scene.add(toRaw(label));
    this.scene.add(toRaw(line));
    this.scene.add(toRaw(xAxisLine));
    this.scene.add(toRaw(xAxisLabel));
    this.scene.add(toRaw(yAxisLine));
    this.scene.add(toRaw(yAxisLabel));
  } else if (object) {
    // only check if auxiliary label overlaps with the first point if it's not already hidden
    if (!this.checkAuxiliaryOverlapWithMainLabel(label, xAxisLabel)) {
      this.checkLabelOverlapWithFirstPoint(xAxisLabel, measurement.firstPoint);
    }

    if (!this.checkAuxiliaryOverlapWithMainLabel(label, yAxisLabel)) {
      this.checkLabelOverlapWithFirstPoint(yAxisLabel, measurement.firstPoint);
    }

    this.checkLabelOverlapWithFirstPoint(label, measurement.firstPoint);
  }
};

export const calculateCoordinatePoints = function (firstPoint, secondPoint) {
  let startPoint, endPoint;
  if (secondPoint.y < firstPoint.y) {
    startPoint = firstPoint;
    endPoint = secondPoint;
  } else {
    startPoint = secondPoint;
    endPoint = firstPoint;
  }

  const junctionPoint = new THREE.Vector3(
    startPoint.x,
    startPoint.y,
    startPoint.z
  );
  return { startPoint, endPoint, junctionPoint };
};

export const checkLabelOverlapWithFirstPoint = function (label, point) {
  const labelScreenPosition = this.getScreenPosition(label);
  const pointScreenPosition = this.getScreenPosition(point);

  const labelWidth = label.element.offsetWidth;
  const labelHeight = label.element.offsetHeight;

  const labelRect = {
    left: labelScreenPosition.x - labelWidth / 2,
    right: labelScreenPosition.x + labelWidth / 2,
    top: labelScreenPosition.y - labelHeight / 2,
    bottom: labelScreenPosition.y + labelHeight / 2,
  };

  const { width: pointWidth, height: pointHeight } = this.getScreenSize(point);
  const pointRect = {
    left: pointScreenPosition.x - pointWidth / 2,
    right: pointScreenPosition.x + pointWidth / 2,
    top: pointScreenPosition.y - pointHeight / 2,
    bottom: pointScreenPosition.y + pointHeight / 2,
  };

  if (this.checkOverlap2(labelRect, pointRect)) {
    label.element.style.visibility = "hidden";
  } else {
    label.element.style.visibility = "visible";
  }
};

export const detectAuxiliaryMeasurementIntersection = function () {
  const expandedMeasurements = this.measurements.filter(
    (measurement) => measurement.xAxisLabel.visible
  );
  for (let measurement of expandedMeasurements) {
    if (
      !this.checkAuxiliaryOverlapWithMainLabel(
        measurement.label,
        measurement.xAxisLabel
      )
    ) {
      this.checkLabelOverlapWithPoint(
        measurement.xAxisLabel,
        measurement.firstPoint
      );
    }

    if (
      !this.checkAuxiliaryOverlapWithMainLabel(
        measurement.label,
        measurement.yAxisLabel
      )
    ) {
      this.checkLabelOverlapWithPoint(
        measurement.yAxisLabel,
        measurement.firstPoint
      );
    }
  }
};

export const checkAuxiliaryOverlapWithMainLabel = function (
  mainLabel,
  secondaryLabel
) {
  const mainLabelRect = mainLabel.element.getBoundingClientRect();
  const secondaryLabelRect = secondaryLabel.element.getBoundingClientRect();

  if (this.checkOverlap(mainLabelRect, secondaryLabelRect)) {
    secondaryLabel.element.style.visibility = "hidden";
    return true;
  } else {
    secondaryLabel.element.style.visibility = "visible";
    return false;
  }
};

export const checkLabelOverlapWithPoint = function (label, point) {
  const labelScreenPosition = this.getScreenPosition(label);
  const pointScreenPosition = this.getScreenPosition(point);

  const labelWidth = label.element.offsetWidth;
  const labelHeight = label.element.offsetHeight;

  const labelRect = {
    left: labelScreenPosition.x - labelWidth / 2,
    right: labelScreenPosition.x + labelWidth / 2,
    top: labelScreenPosition.y - labelHeight / 2,
    bottom: labelScreenPosition.y + labelHeight / 2,
  };

  const { width: pointWidth, height: pointHeight } = this.getScreenSize(point);
  const pointRect = {
    left: pointScreenPosition.x - pointWidth / 2,
    right: pointScreenPosition.x + pointWidth / 2,
    top: pointScreenPosition.y - pointHeight / 2,
    bottom: pointScreenPosition.y + pointHeight / 2,
  };

  if (this.checkOverlap2(labelRect, pointRect)) {
    label.element.style.visibility = "hidden";
  } else {
    label.element.style.visibility = "visible";
  }
};

export const getScreenSize = function (object) {
  const bbox = new THREE.Box3().setFromObject(object);

  const size = new THREE.Vector3();
  bbox.getSize(size);

  const widthHalf = 0.5 * this.renderer.domElement.width;
  const heightHalf = 0.5 * this.renderer.domElement.height;

  const corners = [
    new THREE.Vector3(bbox.min.x, bbox.min.y, bbox.min.z),
    new THREE.Vector3(bbox.min.x, bbox.min.y, bbox.max.z),
    new THREE.Vector3(bbox.min.x, bbox.max.y, bbox.min.z),
    new THREE.Vector3(bbox.min.x, bbox.max.y, bbox.max.z),
    new THREE.Vector3(bbox.max.x, bbox.min.y, bbox.min.z),
    new THREE.Vector3(bbox.max.x, bbox.min.y, bbox.max.z),
    new THREE.Vector3(bbox.max.x, bbox.max.y, bbox.min.z),
    new THREE.Vector3(bbox.max.x, bbox.max.y, bbox.max.z),
  ];

  const min = new THREE.Vector3(Infinity, Infinity, Infinity);
  const max = new THREE.Vector3(-Infinity, -Infinity, -Infinity);

  corners.forEach((corner) => {
    corner.project(this.camera);
    corner.x = corner.x * widthHalf + widthHalf;
    corner.y = -(corner.y * heightHalf) + heightHalf;

    min.min(corner);
    max.max(corner);
  });

  return {
    width: max.x - min.x,
    height: max.y - min.y,
  };
};

export const checkOverlap = function (rect1, rect2) {
  return (
    rect1.x < rect2.x + rect2.width &&
    rect1.x + rect1.width > rect2.x &&
    rect1.y < rect2.y + rect2.height &&
    rect1.y + rect1.height > rect2.y
  );
};

export const checkOverlap2 = function (rect1, rect2) {
  return !(
    rect1.right < rect2.left ||
    rect1.left > rect2.right ||
    rect1.bottom < rect2.top ||
    rect1.top > rect2.bottom
  );
};

export const getScreenPosition = function (object) {
  const vector = new THREE.Vector3();
  const widthHalf = 0.5 * this.renderer.domElement.width;
  const heightHalf = 0.5 * this.renderer.domElement.height;

  object.updateMatrixWorld();
  vector.setFromMatrixPosition(object.matrixWorld);
  vector.project(this.camera);

  vector.x = vector.x * widthHalf + widthHalf;
  vector.y = -(vector.y * heightHalf) + heightHalf;

  return {
    x: vector.x,
    y: vector.y,
  };
};

export const enableDragControls = function () {
  document.addEventListener("mousedown", this.dragMarkerStart);
};

export const disableDragControls = function () {
  document.removeEventListener("mousedown", this.dragMarkerStart);
  document.removeEventListener("mousemove", this.dragMarker);
  document.removeEventListener("mouseup", this.dragMarkerEnd);
};

// A label is a DOM element, so nothing in the depth buffer can hide it the way
// the roof now hides the line it belongs to. A line-of-sight raycast does that
// job instead, against the same BVH the model raycasts already use.
const OCCLUSION_INTERVAL_MS = 100;

// Consecutive agreeing evaluations before an area changes state. At the cadence
// above that is a 200 ms settle: imperceptible, but enough that a sample ray
// grazing a ridge cannot make an area blink on and off.
const OCCLUSION_HYSTERESIS = 2;

// Keyed by the raw area, so an area that is deleted takes its entry with it.
const occlusionState = new WeakMap();
const labelOcclusionRaycaster = new THREE.Raycaster();
labelOcclusionRaycaster.firstHitOnly = true;

const cameraWorldPosition = new THREE.Vector3();
const measurementCentre = new THREE.Vector3();
const labelRayDirection = new THREE.Vector3();
const areaCentre = new THREE.Vector3();
const areaNormal = new THREE.Vector3();
const areaToCamera = new THREE.Vector3();

// Below this the surface is close enough to vertical that "outward points up"
// stops telling us anything, so the facing test is skipped and the raycast
// decides alone. cos(80°) — a roof steeper than that is a wall.
const MIN_FACING_NORMAL_Y = 0.17;

/**
 * How many measurements and areas the pass would judge, counted without
 * building any list — the gate in updateAnnotationOcclusion runs every frame.
 *
 * Counts the same things the collectors collect, not every entry in the arrays:
 * a measurement only counts once it has a label and an area once it has points
 * and something to show. That way finishing a measurement or closing an area
 * changes the count and triggers a pass even with the camera still — which is
 * what hides a new edge length sitting on top of its area's m² label.
 */
function countOccludables(viewer) {
  let count = 0;

  for (const measurement of viewer.measurements ?? []) {
    if (measurement.label?.element) count += 1;
  }

  const countArea = (boundary, fill) => {
    if (boundary?.points?.length && (fill || boundary.label?.element)) count += 1;
  };

  for (const area of viewer.measurementAreas ?? []) countArea(area, area.plane);

  for (const area of viewer.areas ?? []) {
    countArea(area, area.innerPlane);
    for (const restricted of area.restrictedAreas ?? [])
      countArea(restricted, restricted.plane);
  }

  return count;
}

/**
 * Distance measurements that have a label to hide. Each is judged as a whole
 * from its own points — see isMeasurementHiddenByModel.
 *
 * Areas are collected separately, in collectOccludableAreas. The x/y projection
 * labels are not judged at all: their lines are drawn through everything on
 * purpose, so their labels follow.
 */
function collectOccludableMeasurements(viewer) {
  const measurements = [];

  for (const measurement of viewer.measurements ?? []) {
    if (measurement.label?.element) measurements.push(measurement);
  }

  return measurements;
}

// `occluders` is the model, or a list of objects to test together.
function isPointHiddenByModel(
  worldPoint,
  occluders,
  tolerance = OCCLUSION_RAY_TOLERANCE_M
) {
  labelRayDirection.subVectors(worldPoint, cameraWorldPosition).normalize();

  // The point is on or near the roof, so the surface under it is a hit at
  // roughly this distance. Stopping the ray short keeps that self-hit out and
  // leaves only real obstacles.
  const reach = cameraWorldPosition.distanceTo(worldPoint) - tolerance;
  if (reach <= 0) return false;

  labelOcclusionRaycaster.set(cameraWorldPosition, labelRayDirection);
  labelOcclusionRaycaster.far = reach;

  const hits = Array.isArray(occluders)
    ? labelOcclusionRaycaster.intersectObjects(occluders, true)
    : labelOcclusionRaycaster.intersectObject(occluders, true);
  return hits.length > 0;
}

/**
 * The panel arrays that can hide part of a segment: every instanced panel mesh
 * on a solar area. These are depth-tested, so a line passing under a raised
 * array is already drawn faint there, and its label should agree. The legacy
 * single-panel planes are left out: they draw over everything without a depth
 * test, so they never make a line faint.
 */
function collectPanelOccluders(viewer) {
  const meshes = [];
  for (const area of viewer.areas ?? []) {
    for (const mesh of [
      area.instancedMesh,
      area.ewWestMesh,
      area.verticalPanelInstancedMesh,
      area.horizontalPanelInstancedMesh,
    ]) {
      const raw = toRaw(mesh);
      if (raw?.parent && raw.visible) meshes.push(raw);
    }
  }
  return meshes;
}

// Points sampled along a segment, ends included. 11 so that "more than 60%"
// means 7 of 11 (64%), not the 80% that 4 of 5 would.
const SEGMENT_SAMPLES = 11;
const segmentSample = new THREE.Vector3();

/**
 * Whether a segment's value label should go faint: its midpoint is behind the
 * model and more than SEGMENT_LABEL_FAINT_FRACTION of the segment is too.
 *
 * Judged with the tolerance lines are drawn with, and against the same things
 * that make a line faint — the model and the panel arrays — so the label goes
 * faint when its drawn edge mostly is. Cheap in the usual case: a visible
 * midpoint settles it in one ray, and otherwise sampling stops as soon as the
 * answer is certain.
 */
function isSegmentLabelFaint(start, end, occluders) {
  segmentSample.lerpVectors(start, end, 0.5);
  if (!isPointHiddenByModel(segmentSample, occluders, ANNOTATION_DEPTH_TOLERANCE_M))
    return false;

  const needed = Math.floor(SEGMENT_SAMPLES * SEGMENT_LABEL_FAINT_FRACTION) + 1;
  let hidden = 1; // the midpoint
  let remaining = SEGMENT_SAMPLES - 1;

  for (let i = 0; i < SEGMENT_SAMPLES; i++) {
    const t = i / (SEGMENT_SAMPLES - 1);
    if (t === 0.5) continue;

    segmentSample.lerpVectors(start, end, t);
    if (isPointHiddenByModel(segmentSample, occluders, ANNOTATION_DEPTH_TOLERANCE_M))
      hidden += 1;
    remaining -= 1;

    if (hidden >= needed) return true;
    if (hidden + remaining < needed) return false;
  }

  return hidden >= needed;
}

function applyLabelFaint(label, faint) {
  label.element.style.opacity = faint ? String(FAINT_LABEL_OPACITY) : "";
}

/**
 * A distance measurement is hidden only when its midpoint *and* both of its ends
 * are behind the model — the same rule as an area, by centre and corners, so a
 * measurement with one end in view keeps its label.
 *
 * The midpoint is taken from the label's occlusion anchor: the point on the line
 * the label names, before any cosmetic nudge moves the drawn label off it. The
 * label's drawn position is never consulted.
 *
 * There is no facing test, unlike an area: a line has no plane to face with. It
 * does not need one either — a straight line between two points on the same
 * roof face lies on that face, so its midpoint cannot sit on the ridge while
 * both of its ends are hidden behind it.
 */
function isMeasurementHiddenByModel(measurement, model) {
  const label = measurement.label;

  if (label.occlusionAnchor) {
    measurementCentre.copy(label.occlusionAnchor);
  } else {
    label.getWorldPosition(measurementCentre);
  }

  if (!isPointHiddenByModel(measurementCentre, model)) return false;

  for (const end of [measurement.firstPoint, measurement.secondPoint]) {
    if (end?.position && !isPointHiddenByModel(end.position, model)) {
      return false;
    }
  }

  return true;
}

/**
 * Every area the model is allowed to hide, with the fill and the labels that
 * belong to it, so one decision covers all of them.
 *
 * An area is a single thing. Judging each of its labels separately meant an edge
 * label whose midpoint happened to sit on the ridge stayed up while the area it
 * belonged to was hidden behind the roof — a number floating over the wrong side
 * of the building, attached to nothing.
 *
 * `areas[].plane` is not here: it is the zero-opacity margin plane, which has
 * nothing to show.
 */
function collectOccludableAreas(viewer) {
  const areas = [];

  // cornerMesh: the instanced mesh holding this area's corner markers when it is
  // not pointInstancedMesh. It has to be passed per area type, because the field
  // name is overloaded: on a restricted area `instancedMesh` is its corners, but
  // on a solar area `instancedMesh` is the *panels* — enrolling those made every
  // panel stamp the "solid annotation here" stencil bit, which suppressed the
  // faint stretch of any line passing under the array, and drew the panels
  // themselves faintly through the roof.
  const add = (boundary, fill, cornerMesh = null) => {
    if (!boundary?.points?.length) return;

    const labels = [];
    if (boundary.label?.element) labels.push(boundary.label);
    for (const line of boundary.lines ?? []) {
      if (line.label?.element) labels.push(line.label);
    }

    // The outline and the corner/midpoint markers, which get their hidden
    // portions drawn faintly while the area as a whole is in view. Raw objects:
    // the faint pass flips their material state every frame, and doing that
    // through Vue's reactive proxies would trigger reactivity on every write.
    const ghosts = [
      boundary.combinedLine,
      boundary.pointInstancedMesh,
      boundary.midpointInstancedMesh,
      cornerMesh,
    ]
      .filter(Boolean)
      .map((object) => toRaw(object));

    if (!fill && !labels.length && !ghosts.length) return;
    areas.push({ boundary, fill, labels, ghosts });
  };

  for (const area of viewer.measurementAreas ?? []) add(area, area.plane);

  for (const area of viewer.areas ?? []) {
    add(area, area.innerPlane);
    for (const restricted of area.restrictedAreas ?? [])
      add(restricted, restricted.plane, restricted.instancedMesh);
  }

  return areas;
}

/** One term of Newell's method, which gives a polygon's plane normal. */
function accumulateNewell(from, to, out) {
  out.x += (from.y - to.y) * (from.z + to.z);
  out.y += (from.z - to.z) * (from.x + to.x);
  out.z += (from.x - to.x) * (from.y + to.y);
}

/**
 * The area's outward plane normal, or null if its points do not define a plane.
 *
 * Newell's method rather than a cross product of the first three points: it uses
 * every vertex, so it is stable on a non-convex outline and on points that are
 * nearly collinear. Its sign depends on winding order, which nothing here
 * guarantees, so it is resolved by the one thing that is always true of a roof
 * face — outward is up.
 */
function computeAreaNormal(points, out) {
  out.set(0, 0, 0);

  let first = null;
  let previous = null;
  let counted = 0;

  for (const point of points) {
    const position = point?.position;
    if (!position) continue;

    if (!first) first = position;
    if (previous) accumulateNewell(previous, position, out);

    previous = position;
    counted += 1;
  }

  if (counted < 3) return null;

  accumulateNewell(previous, first, out);
  if (out.lengthSq() === 0) return null;

  out.normalize();
  if (out.y < 0) out.negate();

  return out;
}

/**
 * An area is hidden when it faces away from the camera, or when its centre *and*
 * every corner are behind the model.
 *
 * The facing test is what settles the far side of the building, and a raycast
 * cannot do it: an area's top edge usually runs along the ridge, and a point on
 * the ridge is on the silhouette — genuinely visible from both sides. So "any
 * corner in view" kept the whole area up when only its ridge corners could be
 * seen. Which side of the surface the camera is on has no such ambiguity.
 *
 * The raycast still earns its place for the case the facing test cannot see: an
 * area that faces the camera but sits behind a dormer, a chimney or the next
 * building. There, erring toward showing it matters — an area with one corner in
 * view is something the user is looking at. Its labels are never consulted;
 * where a number happens to sit says nothing about whether the area is in view.
 */
function isAreaHiddenByModel(boundary, model) {
  const points = boundary.points;

  areaCentre.set(0, 0, 0);
  let counted = 0;
  for (const point of points) {
    if (!point?.position) continue;
    areaCentre.add(point.position);
    counted += 1;
  }
  if (!counted) return false;

  areaCentre.divideScalar(counted);

  const normal = computeAreaNormal(points, areaNormal);
  if (normal && Math.abs(normal.y) >= MIN_FACING_NORMAL_Y) {
    areaToCamera.subVectors(cameraWorldPosition, areaCentre);
    if (normal.dot(areaToCamera) <= 0) return true;
  }

  if (!isPointHiddenByModel(areaCentre, model)) return false;

  for (const point of points) {
    if (point?.position && !isPointHiddenByModel(point.position, model)) {
      return false;
    }
  }

  return true;
}

/**
 * Settles a raw yes/no into a state that only changes once the same answer has
 * come back OCCLUSION_HYSTERESIS times running, and returns the settled value.
 */
function resolveOcclusion(key, hidden) {
  const state = occlusionState.get(key) ?? { hidden: false, pending: 0 };

  if (hidden === state.hidden) {
    state.pending = 0;
  } else if (++state.pending >= OCCLUSION_HYSTERESIS) {
    state.hidden = hidden;
    state.pending = 0;
  }

  if (state.pending > 0) settlingInLastPass += 1;

  occlusionState.set(key, state);
  return state.hidden;
}

// How many decisions the last pass left half-made. While this is non-zero the
// pass keeps running after the camera stops, so a change that was mid-way
// through its hysteresis when the camera came to rest still gets committed.
let settlingInLastPass = 0;

/**
 * Whether an area's edge-length label sits on top of the area's own m²/angle
 * label on screen, in which case the edge length gives way.
 *
 * This used to run from a wheel listener (hideAuxiliaryValuesOnZoom) that wrote
 * `style.visibility` itself. The occlusion pass writes the same property, so on
 * every scroll tick the two overwrote each other — one showing a label, the
 * other hiding it — which is what made labels blink while zooming. It also only
 * ran on the wheel, before camera-controls had finished easing the zoom, so it
 * judged the overlap at a stale position and never re-judged it after an orbit
 * or a pan. Folding it in here gives every label exactly one writer, judged at
 * the camera the user actually ends up at.
 */
function coversAreaLabel(viewer, edgeLabel, areaLabel) {
  if (!areaLabel?.element || edgeLabel === areaLabel) return false;

  return viewer.checkOverlap(
    areaLabel.element.getBoundingClientRect(),
    edgeLabel.element.getBoundingClientRect()
  );
}

/**
 * Moves a fill on or off the hidden layer, but only once the same answer has
 * come back OCCLUSION_HYSTERESIS times running. Layers are used rather than
 * `visible` because the show/hide-area logic already owns `visible`, and this
 * must not fight it.
 */
/**
 * Leaves a label that something else has already hidden alone, but clears our
 * own flag so it comes back intact when that other reason goes away.
 */
function applyLabelOcclusion(label, hidden) {
  label.element.style.visibility =
    hidden && label.visible !== false ? "hidden" : "";
}

/**
 * Hides labels and area fills that the model stands in front of, so neither can
 * outlive the thing it belongs to.
 *
 * Labels are DOM and fills are flat approximations of a surface that is not
 * flat, so neither can be settled by the depth buffer — both are decided here by
 * a line-of-sight raycast against the same BVH the model raycasts already use.
 *
 * An area is decided once, from its centre and corners, and that one answer
 * drives its fill and all of its labels together. A distance measurement is
 * decided the same way, from its midpoint and both ends, and that answer drives
 * its label. Either way a label's own position is never consulted.
 *
 * Runs from the animation loop, so it is deliberately cheap. It is rate-limited,
 * and skipped outright unless something that can change the answer has moved.
 * Camera *rotation* is not one of those things: occlusion depends only on the
 * ray from the camera's position to the target, so turning on the spot cannot
 * change it, and anything already hidden for another reason costs no raycast.
 *
 * Labels are switched with `style.visibility` and fills with layers, because
 * CSS2DRenderer, the show/hide-details logic and the show/hide-area logic
 * already own `style.display` and `visible` between them.
 */
export const updateAnnotationOcclusion = function () {
  // Cheapest checks first. This runs on every animation frame, and most frames
  // end at the throttle or the camera-moved gate below — so nothing is
  // collected or allocated until a pass is actually going to run.
  const model = this.modelObject?.children?.[0];
  if (!model) return;

  const camera = toRaw(this.camera);
  if (!camera) return;

  const now = performance.now();
  if (now - (this.lastOcclusionAt ?? 0) < OCCLUSION_INTERVAL_MS) return;

  camera.getWorldPosition(cameraWorldPosition);

  const count = countOccludables(this);
  if (!count) return;

  const unchanged =
    !this.dragOn &&
    this.lastOcclusionCount === count &&
    this.lastOcclusionFrom?.equals(cameraWorldPosition);

  // Stop only once the camera has stopped, one pass has run at the resting
  // position, and nothing is still part-way through its hysteresis. Stopping on
  // the first still frame left whatever the last moving frame decided — judged
  // up to one throttle interval before the camera actually came to rest.
  if (unchanged && this.occlusionSettled && settlingInLastPass === 0) return;

  this.occlusionSettled = unchanged;
  settlingInLastPass = 0;

  this.lastOcclusionAt = now;
  this.lastOcclusionCount = count;
  this.lastOcclusionFrom = (
    this.lastOcclusionFrom ?? new THREE.Vector3()
  ).copy(cameraWorldPosition);

  const measurements = collectOccludableMeasurements(this);
  const areas = collectOccludableAreas(this);

  // Segment labels are judged against panels as well as the model, like the
  // lines under them. Everything else here — hidden as a whole, and the picking
  // that follows it — stays model-only: an area drawn on an array must not be
  // judged hidden just because the panels cover it.
  const labelOccluders = [model, ...collectPanelOccluders(this)];

  for (const measurement of measurements) {
    const label = measurement.label;

    // A label already hidden for another reason costs no ray, and its flag is
    // cleared so it comes back intact.
    if (label.visible === false) {
      applyLabelOcclusion(label, false);
      applyLabelFaint(label, false);
      continue;
    }

    const hidden = resolveOcclusion(
      toRaw(measurement),
      isMeasurementHiddenByModel(measurement, model)
    );
    applyLabelOcclusion(label, hidden);

    // Selected and in view: shown at full strength, hidden stretches included.
    const emphasized = !hidden && isAnnotationSelected(measurement);

    // In view as a whole: the value goes faint once its segment mostly is —
    // unless the measurement is selected.
    applyLabelFaint(
      label,
      !hidden &&
        !emphasized &&
        resolveOcclusion(
          toRaw(label),
          isSegmentLabelFaint(
            toRaw(measurement.firstPoint).position,
            toRaw(measurement.secondPoint).position,
            labelOccluders
          )
        )
    );

    // Partly in view: the hidden stretches of the line and ends stay faint, as
    // every line and point is from creation. Wholly hidden: nothing, as before.
    for (const object of [
      measurement.line,
      measurement.firstPoint,
      measurement.secondPoint,
    ]) {
      if (!object) continue;
      setOccludedGhostVisible(toRaw(object), !hidden);
      setOccludedEmphasis(object, emphasized);
    }
  }

  for (const { boundary, fill, labels: areaLabels, ghosts } of areas) {
    const hidden = resolveOcclusion(
      toRaw(boundary),
      isAreaHiddenByModel(boundary, model)
    );

    if (fill) {
      fill.layers.set(hidden ? OCCLUSION_HIDDEN_LAYER : 0);
      // set() clears every other layer, including the faint one — put it back
      // for a measurement area's body (a no-op for fills not in the faint pass).
      if (!hidden) setOccludedGhostVisible(toRaw(fill), true);
    }

    // Same rule as the fill: an area in view keeps the faint hidden parts of its
    // outline and corners; an area hidden as a whole loses them too. A selected
    // area in view shows them, and its body's, at full strength.
    const emphasized = !hidden && isAnnotationSelected(boundary);
    for (const object of ghosts) {
      setOccludedGhostVisible(object, !hidden);
      setOccludedEmphasis(object, emphasized);
    }
    if (fill) setOccludedEmphasis(fill, emphasized);

    for (const label of areaLabels) {
      applyLabelOcclusion(
        label,
        hidden || coversAreaLabel(this, label, boundary.label)
      );
    }

    // Each edge length goes faint once its edge mostly is. The area/angle label
    // stays on the area-level rule: it describes the whole area, whose body
    // already shows its own hidden parts faintly.
    for (const line of boundary.lines ?? []) {
      const label = line.label;
      if (!label?.element || !line.firstPoint || !line.secondPoint) continue;

      applyLabelFaint(
        label,
        !hidden &&
          !emphasized &&
          label.visible !== false &&
          resolveOcclusion(
            toRaw(label),
            isSegmentLabelFaint(
              toRaw(line.firstPoint).position,
              toRaw(line.secondPoint).position,
              labelOccluders
            )
          )
      );
    }
  }
};

/**
 * Measurement labels all share RENDERING_ORDER.MEASUREMENT_LABEL so that
 * CSS2DRenderer's distance tie-break decides which one stacks on top. Selecting
 * one lifts it out of that tie for as long as it stays selected, which is the
 * only case where "keep this label in front" beats "keep the nearest label in
 * front".
 */
export const setMeasurementLabelSelected = function (measurement, selected) {
  const boost = selected ? SELECTED_LABEL_BOOST : 0;

  // A selected measurement is shown at full strength even where it is hidden —
  // see updateAnnotationOcclusion. Invalidate its gate so that applies on the
  // next frame rather than the next time the camera moves.
  if (measurement) {
    setAnnotationSelected(measurement, selected);
    this.lastOcclusionCount = -1;
  }

  if (measurement?.label) {
    measurement.label.renderOrder = RENDERING_ORDER.MEASUREMENT_LABEL + boost;
  }

  // The x/y projection readouts only appear for the selected measurement, so
  // they ride along — left behind, an unselected area's label would stack over
  // them.
  for (const axisLabel of [measurement?.xAxisLabel, measurement?.yAxisLabel]) {
    if (axisLabel) {
      axisLabel.renderOrder = RENDERING_ORDER.AUXILIARY_LABEL + boost;
    }
  }
};

export const showMeasurementDetails = function (
  measurement,
  showRemoveIcon = false,
  fromSidebar = false,
  resetMode = true
) {
  if (showRemoveIcon) {
    if (this.active !== 0 && this.active !== 1 && !fromSidebar) return;
    if (this.active !== 1 && resetMode) {
      this.toggleActive(0);
    }
    let filteredMeasurements = this.measurements.filter((element) => {
      return element.label?.id !== measurement?.label?.id;
    });
    const foundIndex = this.measurements.findIndex((element) => {
      return element.label?.id === measurement?.label?.id;
    });
    if (!fromSidebar) {
      this.unselectArea();

      if (hasPermission(Permission.Update_Objects)) {
        this.selectMeasurementFromCanvas(foundIndex);
      }
    }
    if (filteredMeasurements.length > 0) {
      this.selectedMeasurement = measurement;
      this.hideMeasurementDetails(filteredMeasurements);
    }
    this.isMeasurementSelected = true;
  }

  if (!measurement) return;

  if (showRemoveIcon) this.setMeasurementLabelSelected(measurement, true);

  const xAxisLine = measurement.xAxisLine;
  const yAxisLine = measurement.yAxisLine;
  const xAxisLabel = measurement.xAxisLabel;
  const yAxisLabel = measurement.yAxisLabel;
  const firstPoint = measurement.firstPoint;
  const label = measurement.label;

  const xLineObject = this.scene.getObjectById(xAxisLine.id);
  const firstPointObject = this.scene.getObjectById(firstPoint.id);

  const xLabelObject = this.scene.getObjectById(xAxisLabel.id);
  const yLabelObject = this.scene.getObjectById(yAxisLabel.id);

  if (firstPointObject.visible) {
    if (xLineObject) {
      const yLineObject = this.scene.getObjectById(yAxisLine.id);

      xLineObject.visible = true;
      yLineObject.visible = true;

      xLabelObject.visible = true;
      yLabelObject.visible = true;
    } else {
      this.scene.add(
        toRaw(xAxisLine),
        toRaw(yAxisLine),
        toRaw(xAxisLabel),
        toRaw(yAxisLabel)
      );
    }
    if (showRemoveIcon) {
      label.element.children[1].style.display = "inline";
      this.setPointColor(measurement.firstPoint, viewerColors.SELECTED_LINE_COLOR);
      this.setPointColor(measurement.secondPoint, viewerColors.SELECTED_LINE_COLOR);
      this.setLineColor(measurement.line, viewerColors.SELECTED_LINE_COLOR);
    }
  }
};

export const hideMeasurementDetails = function (
  filteredMeasurements,
  hover = false
) {
  const measurementsToHide = filteredMeasurements || this.measurements;

  if (this.isMeasurementSelected && !hover) {
    this.isMeasurementSelected = false;
  }

  for (let measurement of measurementsToHide) {
    // Unconditional: the label keeps its selected rung even when the axis lines
    // below were never added to the scene.
    this.setMeasurementLabelSelected(measurement, false);

    const xAxisLine = measurement.xAxisLine;
    const yAxisLine = measurement.yAxisLine;
    const xAxisLabel = measurement.xAxisLabel;
    const yAxisLabel = measurement.yAxisLabel;
    const label = measurement.label;

    const xLineObject = this.scene.getObjectById(xAxisLine.id);
    if (xLineObject) {
      const yLineObject = this.scene.getObjectById(yAxisLine.id);
      const xLabelObject = this.scene.getObjectById(xAxisLabel.id);
      const yLabelObject = this.scene.getObjectById(yAxisLabel.id);

      xLineObject.visible = false;
      yLineObject.visible = false;
      xLabelObject.visible = false;
      yLabelObject.visible = false;
      if (label) {
        label.element.children[1].style.display = "none";
        label.element.className = "measurementLabel";
      }

      if (measurement.firstPoint)
        this.setPointColor(measurement.firstPoint, viewerColors.POINT_COLOR);
      if (measurement.secondPoint)
        this.setPointColor(measurement.secondPoint, viewerColors.POINT_COLOR);
      if (measurement.line) this.setLineColor(measurement.line, viewerColors.POINT_COLOR);
    }
  }
};

export const showMeasurement = function (lineId, resetMode = true) {
  if (this.active !== 1 && this.active !== 0) {
    return;
  }
  const measurement = this.measurements.find(
    (measurement) => measurement.line.id == lineId
  );

  this.showMeasurementDetails(measurement, true, false, resetMode);
  this.measurementDetailsPersistent = true;
  this.measurementLabelClicked = true;
};
export const showMeasurementFromSidebar = function (lineId) {
  if (lineId) {
    const fromSidebar = true;
    const measurement = this.measurements.find(
      (measurement) => measurement.line.id == lineId
    );
    this.showMeasurementDetails(measurement, true, fromSidebar);
    this.measurementDetailsPersistent = true;
    this.measurementLabelClicked = true;
  } else {
    this.hideMeasurementDetails(this.measurements);
  }
};

export const deleteMeasurement = function (lineId) {
  const measurement = this.measurements.find(
    (measurement) => measurement.line.id == lineId
  );

  this.removeObjectFromScene(measurement.line);
  this.removeObjectFromScene(measurement.label);
  this.removeObjectFromScene(measurement.firstPoint);
  this.removeObjectFromScene(measurement.secondPoint);
  this.removeObjectFromScene(measurement.xAxisLine);
  this.removeObjectFromScene(measurement.xAxisLabel);
  this.removeObjectFromScene(measurement.yAxisLine);
  this.removeObjectFromScene(measurement.yAxisLabel);

  this.deleteMeasurementObject(measurement.id);

  this.measurements = this.measurements.filter(
    (m) => m.line.id !== measurement.line.id
  );

  this.undoStack.push({
    action: "DELETE_MEASUREMENT",
    measurement: {
      position: [
        {
          x: measurement.firstPoint.position.x,
          y: measurement.firstPoint.position.y,
          z: measurement.firstPoint.position.z,
        },
        {
          x: measurement.secondPoint.position.x,
          y: measurement.secondPoint.position.y,
          z: measurement.secondPoint.position.z,
        },
      ],
      show: true,
      name: measurement.name,
    },
  });
  this.resetRedoStack();
};

export const dragMarkerStart = function (event) {
  this.setMousePosition(event);

  const closedMeasurements = this.measurements.filter(
    (measurement) => measurement.secondPoint && measurement.show !== false
  );
  const points = closedMeasurements.flatMap((measurement) => [
    measurement.firstPoint,
    measurement.secondPoint,
  ]);

  const pointIntersects = dropHitsOnHiddenAnnotations(
    this.raycaster.intersectObjects(points, true)
  );

  if (pointIntersects.length === 0) return;

  let draggedMarker = pointIntersects[0].object;
  let measurement = this.measurements.find(
    (m) =>
      m.firstPoint.id === draggedMarker.id ||
      m.secondPoint.id === draggedMarker.id
  );
  let isFirstPoint = measurement.firstPoint.id === draggedMarker.id;

  if (!measurement) return;

  if (
    this.existingMeasurementsIds.includes(measurement.id) &&
    !hasPermission(Permission.Update_Objects)
  ) {
    return;
  }

  this.holdDistanceMeasurement();
  // this is used to prevent selecting area measurement after dragging distance measurement
  this.measurementDrag = true;

  this.draggedMeasurementPoint = {
    measurement,
    isFirstPoint,
    draggedMarker,
  };

  draggedMarker.prevPosition = {
    x: draggedMarker.position.x,
    y: draggedMarker.position.y,
    z: draggedMarker.position.z,
  };
  draggedMarker.lastPosition = {
    x: draggedMarker.position.x,
    y: draggedMarker.position.y,
    z: draggedMarker.position.z,
  };

  this.showMeasurement(measurement.line.id, false);

  this.dragOn = true;

  document.addEventListener("mousemove", this.dragMarker);
  document.addEventListener("mouseup", this.dragMarkerEnd);
};

export const dragMarker = function (event) {
  if (this.shouldThrottle()) return;

  if (!this.dragOn) return;

  this.setMousePosition(event);

  this.handleDragDistanceMeasurementRaycastResult(this.raycastModelPoint());
};

export const dragMarkerEnd = function () {
  const { measurement, draggedMarker } = this.draggedMeasurementPoint;

  this.unholdDistanceMeasurement(measurement);

  this.updateMeasurementObject(measurement.id, [
    {
      x: measurement.firstPoint.position.x,
      y: measurement.firstPoint.position.y,
      z: measurement.firstPoint.position.z,
    },
    {
      x: measurement.secondPoint.position.x,
      y: measurement.secondPoint.position.y,
      z: measurement.secondPoint.position.z,
    },
  ]);

  this.undoStack.push({
    action: "MOVE_MEASUREMENT_POINT",
    measurement: measurement,
    point: draggedMarker,
  });
  setTimeout(() => {
    this.measurementDrag = false;
    this.dragOn = false;
    this.draggedMeasurementPoint = null;

    document.removeEventListener("mousemove", this.dragMarker);
    document.removeEventListener("mouseup", this.dragMarkerEnd);
  }, 0);
  this.resetRedoStack();
};

export const reDraw = function (measurement) {
  const firstPoint = new THREE.Vector3();
  const secondPoint = new THREE.Vector3();

  measurement.firstPoint.getWorldPosition(firstPoint);
  measurement.secondPoint.getWorldPosition(secondPoint);

  let points = [firstPoint, secondPoint];

  this.updateLinePosition(measurement.line, points);

  this.updateLabelBetweenTwoPoints(
    measurement.label,
    firstPoint,
    secondPoint,
    0.5,
    false,
    true,
    measurement.line
  );
  measurement.distance = measurement.label.element.distance;

  const xAxisLine = measurement.xAxisLine;
  const xAxisLabel = measurement.xAxisLabel;

  const yAxisLine = measurement.yAxisLine;
  const yAxisLabel = measurement.yAxisLabel;

  this.updateCoordinateMeasurementLine(
    xAxisLine,
    xAxisLabel,
    firstPoint,
    new THREE.Vector3(firstPoint.x, secondPoint.y, firstPoint.z)
  );

  this.updateCoordinateMeasurementLine(
    yAxisLine,
    yAxisLabel,
    new THREE.Vector3(firstPoint.x, secondPoint.y, firstPoint.z),
    new THREE.Vector3(secondPoint.x, secondPoint.y, secondPoint.z)
  );

  if (!this.checkAuxiliaryOverlapWithMainLabel(measurement.label, xAxisLabel)) {
    this.checkLabelOverlapWithFirstPoint(xAxisLabel, measurement.firstPoint);
  }

  if (!this.checkAuxiliaryOverlapWithMainLabel(measurement.label, yAxisLabel)) {
    this.checkLabelOverlapWithFirstPoint(yAxisLabel, measurement.firstPoint);
  }

  this.checkLabelOverlapWithFirstPoint(
    measurement.label,
    measurement.firstPoint
  );
};

export const handleDragDistanceMeasurementRaycastResult = function (
  intersectionPoint
) {
  if (!intersectionPoint || !this.draggedMeasurementPoint?.draggedMarker)
    return;

  let pIntersect = new THREE.Vector3(
    intersectionPoint.x,
    intersectionPoint.y,
    intersectionPoint.z
  );

  this.scene.worldToLocal(pIntersect);

  const { draggedMarker, measurement } = this.draggedMeasurementPoint;

  draggedMarker.intersectionPoint = intersectionPoint;

  draggedMarker.position.x = intersectionPoint.x;
  draggedMarker.position.y = intersectionPoint.y;
  draggedMarker.position.z = intersectionPoint.z;

  draggedMarker.prevPosition = {
    x: draggedMarker.position.x,
    y: draggedMarker.position.y,
    z: draggedMarker.position.z,
  };

  this.reDraw(measurement);
};

export const displayMarker = function (measurement, point, fromUndo = false) {
  const marker = this.createMeasurementMarker(point);

  const measurementId = measurement.id;
  // first point placement
  if (
    this.measurements.length === 0 ||
    this.measurements[this.measurements.length - 1].secondPoint
  ) {
    this.measurements.push({ id: measurementId, firstPoint: marker, show: true });
    // second point placement
  } else {
    let currentMeasurement = this.measurements[this.measurements.length - 1];

    currentMeasurement.secondPoint = marker;

    const firstPoint = new THREE.Vector3();
    const secondPoint = new THREE.Vector3();

    currentMeasurement.firstPoint.getWorldPosition(firstPoint);
    currentMeasurement.secondPoint.getWorldPosition(secondPoint);

    let points = [firstPoint, secondPoint];
    let line = this.createReactiveThickLine(points, 4.0);
    let label = this.createLabelBetweenTwoPoints(
      firstPoint,
      secondPoint,
      0.5,
      true,
      line
    );
    this.scene.add(line);
    this.scene.add(label);

    currentMeasurement.line = line;
    currentMeasurement.show = measurement.show;
    currentMeasurement.label = label;
    currentMeasurement.distance = label.element.distance;

    const {
      line: xAxisLine,
      label: xAxisLabel,
      distance: verticalComponent,
    } = this.createCoordinateMeasurementLine(
      firstPoint,
      new THREE.Vector3(firstPoint.x, secondPoint.y, firstPoint.z),
      COORDINTATE_LINE_COLOR_X,
      true
    );
    currentMeasurement.verticalComponent = verticalComponent.toFixed(2);

    const {
      line: yAxisLine,
      label: yAxisLabel,
      distance: horizontalComponent,
    } = this.createCoordinateMeasurementLine(
      new THREE.Vector3(firstPoint.x, secondPoint.y, firstPoint.z),
      new THREE.Vector3(secondPoint.x, secondPoint.y, secondPoint.z),
      COORDINTATE_LINE_COLOR_Y,
      false
    );
    currentMeasurement.horizontalComponent = horizontalComponent.toFixed(2);

    this.scene.add(xAxisLine);
    this.scene.add(xAxisLabel);
    this.scene.add(yAxisLine);
    this.scene.add(yAxisLabel);

    currentMeasurement.xAxisLine = xAxisLine;
    currentMeasurement.yAxisLine = yAxisLine;
    currentMeasurement.xAxisLabel = xAxisLabel;
    currentMeasurement.yAxisLabel = yAxisLabel;

    if (currentMeasurement.show === false) {
      hideSelectedMeasurement(currentMeasurement);
    }

    if (!fromUndo) this.toggleActive(0);
  }
  this.markers.push(marker);

  if (hasPermission(Permission.Update_Objects)) {
    this.disableDragControls();
    this.enableDragControls();
  }

  this.scene.add(marker);
};
export const removeUnfinishedMeasurements = function () {
  const measurement = this.measurements[this.measurements.length - 1];
  if (measurement && !measurement.secondPoint) {
    const firstPoint = measurement.firstPoint;
    const tempLine = measurement.tempLine;
    const tempLabel = measurement.tempLabel;
    const xAxisLine = measurement.xAxisLine;
    const yAxisLine = measurement.yAxisLine;
    const xAxisLabel = measurement.xAxisLabel;
    const yAxisLabel = measurement.yAxisLabel;

    const firstPointObject = this.scene.getObjectById(firstPoint.id);
    const tempLineObject = this.scene.getObjectById(tempLine.id);
    const tempLabelObject = this.scene.getObjectById(tempLabel.id);
    const xAxisLineObject = this.scene.getObjectById(xAxisLine.id);
    const yAxisLineObject = this.scene.getObjectById(yAxisLine.id);
    const xAxisLabelObject = this.scene.getObjectById(xAxisLabel.id);
    const yAxisLabelObject = this.scene.getObjectById(yAxisLabel.id);

    this.scene.remove(
      firstPointObject,
      tempLineObject,
      tempLabelObject,
      xAxisLineObject,
      yAxisLineObject,
      xAxisLabelObject,
      yAxisLabelObject
    );

    this.measurements.pop();
  }
  this.disableDragControls();
  this.enableDragControls();
};

export const removeMeasurements = function () {
  if (this.measurements.length > 0) {
    this.measurements.forEach((measurement) => {
      this.deleteMeasurement(measurement.line.id);
    });
    this.measurements = [];
  }
};

export const hideMeasurements = function () {
  if (this.measurements.length > 0) {
    this.measurements.forEach((measurement) => {
      hideSelectedMeasurement(measurement);
    });
  }
};

export const showMeasurements = function () {
  if (this.measurements.length > 0) {
    this.measurements.forEach((measurement) => {
      if (measurement.show) {
        showSelectedMeasurement(measurement);
      }
    });
  }
};

export const hideSelectedMeasurement = function (selectedMeasurement) {
  // Hide the first point
  if (selectedMeasurement.firstPoint) {
    selectedMeasurement.firstPoint.visible = false;
  }

  // Hide the second point
  if (selectedMeasurement.secondPoint) {
    selectedMeasurement.secondPoint.visible = false;
  }

  // Hide the line
  if (selectedMeasurement.line) {
    selectedMeasurement.line.visible = false;
  }

  // Hide the label
  if (selectedMeasurement.label) {
    selectedMeasurement.label.visible = false;
  }
  if (selectedMeasurement.xAxisLabel) {
    selectedMeasurement.xAxisLabel.visible = false;
  }
  if (selectedMeasurement.yAxisLabel) {
    selectedMeasurement.yAxisLabel.visible = false;
  }
  if (selectedMeasurement.xAxisLine) {
    selectedMeasurement.xAxisLine.visible = false;
  }
  if (selectedMeasurement.yAxisLine) {
    selectedMeasurement.yAxisLine.visible = false;
  }
};
export const showSelectedMeasurement = function (selectedMeasurement) {
  // Show the first point
  if (selectedMeasurement.firstPoint) {
    selectedMeasurement.firstPoint.visible = true;
  }

  // Show the second point
  if (selectedMeasurement.secondPoint) {
    selectedMeasurement.secondPoint.visible = true;
  }

  // Show the line
  if (selectedMeasurement.line) {
    selectedMeasurement.line.visible = true;
  }

  // Show the label
  if (selectedMeasurement.label) {
    selectedMeasurement.label.visible = true;
  }
};

export const createMeasurementObject = async function (points) {
  if (!hasPermission(Permission.Create_Objects)) return;
  const measurementObject = {
    projectId: Number(this.projectId),
    position: points,
  };
  return await API.airteam3DViewer.createMeasurementObject(measurementObject);
};

export const updateMeasurementObject = async function (id, points) {
  if (!hasPermission(Permission.Update_Objects)) return;
  const measurementObject = {
    id,
    projectId: Number(this.projectId),
    position: points,
  };
  return await API.airteam3DViewer.updateMeasurementObject(measurementObject);
};

export const deleteMeasurementObject = async function (id) {
  if (!hasPermission(Permission.Update_Objects)) return;
  return await API.airteam3DViewer.deleteObject(id);
};

export const reAddMarker = function () {
  document.removeEventListener("click", this.reAddMarker, false);
  document.addEventListener("click", this.addMarker, false);
};

export const undoAddMeasurementPoint = function (measurement) {
  this.redoStack.push({
    action: "ADD_MEASUREMENT_POINT",
    measurement,
  });

  const point = measurement.firstPoint;
  const tempLine = measurement.tempLine;
  const tempLabel = measurement.tempLabel;
  const xAxisLine = measurement.xAxisLine;
  const yAxisLine = measurement.yAxisLine;
  const xAxisLabel = measurement.xAxisLabel;
  const yAxisLabel = measurement.yAxisLabel;

  this.markers = this.markers.filter((marker) => marker.id !== point.id);
  this.measurements = this.measurements.slice(0, this.measurements.length - 1);

  this.hideObjectFromScene(point);
  if (tempLine) this.hideObjectFromScene(tempLine);
  if (tempLabel) this.hideObjectFromScene(tempLabel);
  if (xAxisLine) this.hideObjectFromScene(xAxisLine);
  if (xAxisLabel) this.hideObjectFromScene(yAxisLine);
  if (yAxisLine) this.hideObjectFromScene(xAxisLabel);
  if (yAxisLabel) this.hideObjectFromScene(yAxisLabel);

  this.tempLine = null;
};

export const redoAddMeasurementPoint = function (measurement) {
  const point = measurement.firstPoint;
  const tempLine = measurement.tempLine;
  const tempLabel = measurement.tempLabel;
  const xAxisLine = measurement.xAxisLine;
  const yAxisLine = measurement.yAxisLine;
  const xAxisLabel = measurement.xAxisLabel;
  const yAxisLabel = measurement.yAxisLabel;

  this.markers.push(point);
  this.measurements.push(measurement);

  point.visible = true;
  if (tempLine) tempLine.visible = true;
  if (tempLabel) tempLabel.visible = true;
  if (xAxisLine) xAxisLine.visible = true;
  if (xAxisLabel) yAxisLine.visible = true;
  if (yAxisLine) xAxisLabel.visible = true;
  if (yAxisLabel) yAxisLabel.visible = true;

  this.tempLine = tempLine;

  this.undoStack.push({
    action: "ADD_MEASUREMENT_POINT",
    measurement,
  });
};

export const removeLastDistancePoint = function (event) {
  const key = event.key;
  if (key !== "Backspace" && key !== "Delete") return;

  // Backspace in a text field must edit the text, not undo the drawing.
  const target = event.target;
  if (
    this.keyboardShortcutsSuspended ||
    (target &&
      (target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable))
  ) {
    return;
  }

  if (this.measurements.length === 0) return;
  this.removeUnfinishedMeasurements();
};

export const updateMeasurementLabelCursors = function (active) {
  // Get all measurement labels
  const measurementLabels = document.querySelectorAll(".measurementLabel");

  measurementLabels.forEach((label) => {
    if (active === 0 || active === 1) {
      label.style.cursor = "pointer";
    } else {
      label.style.cursor = "default"; // or 'inherit' or whatever default cursor you want
    }
  });
};
